import type { BlockerFunction } from 'react-router';

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

import type { BlockableLocationState } from '~/constants/router';

import { statusColorThemes } from '~/styles/themes.css';

import { Button } from '../Button/Button';
import { Dialog } from '../Dialog/Dialog';
import { useBrowserBlocker } from './Blocker.hooks';

type Props = {
  isBlocked: boolean | BlockerFunction;
  leaveText?: string;
  message: string;
  stayText?: string;
};

export const Blocker = ({ isBlocked, leaveText, message, stayText }: Props) => {
  useBrowserBlocker(typeof isBlocked === 'boolean' ? isBlocked : true);

  const shouldBlock: BlockerFunction = useCallback(
    (args) => {
      const { blockable } = (args.nextLocation.state ?? {}) as BlockableLocationState;

      // Allow bypassing the blocker for imperative navigations
      // by adding `{ blockable: false }` to the location state
      if (blockable === false) {
        return false;
      }

      return typeof isBlocked === 'boolean' ? isBlocked : isBlocked(args);
    },
    [isBlocked],
  );

  const blocker = useBlocker(shouldBlock);

  const { t } = useTranslation(['common']);

  useEffect(() => {
    if (blocker.state === 'blocked' && isBlocked === false) blocker.reset();
  }, [blocker, isBlocked]);

  return (
    <Dialog.Root onOpenChange={() => blocker.reset?.()} open={blocker.state !== 'unblocked'}>
      <Dialog.Body>
        <Dialog.Header title={t('common:blocker.title')} />

        <p>{message}</p>

        <Dialog.Actions>
          <Button emphasis="secondary" onClick={() => blocker.reset?.()}>
            {stayText ?? t('common:blocker.stay')}
          </Button>

          <Button className={statusColorThemes.danger} emphasis="status" onClick={() => blocker.proceed?.()}>
            {leaveText ?? t('common:blocker.leave')}
          </Button>
        </Dialog.Actions>
      </Dialog.Body>
    </Dialog.Root>
  );
};
